import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FiSearch, FiZap, FiPlus } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserCampaigns } from '../../../hooks/useUserCampaigns';

export default function EnrollCampaignModal({
  isOpen,
  onClose,
  enrolledCampaignIds,
  onEnroll,
}) {
  const { currentUser } = useAuth();
  const { campaigns, loading } = useUserCampaigns(currentUser?.uid);
  const [search, setSearch] = useState('');
  const [enrollingId, setEnrollingId] = useState(null);
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const availableCampaigns = useMemo(() => {
    const enrolled = new Set(enrolledCampaignIds || []);
    return campaigns
      .filter((c) => c.isEnabled && !enrolled.has(c.id))
      .filter((c) => {
        if (!search.trim()) return true;
        const name = (c.template?.name || '').toLowerCase();
        return name.includes(search.toLowerCase());
      });
  }, [campaigns, enrolledCampaignIds, search]);

  const enrolledCount = campaigns.filter(
    (c) => c.isEnabled && (enrolledCampaignIds || new Set()).has(c.id)
  ).length;

  const handleEnroll = async (userCampaignId) => {
    setEnrollingId(userCampaignId);
    try {
      await onEnroll(userCampaignId);
      toast({ title: 'Enrolled in campaign', status: 'success', duration: 2000 });
      onClose();
    } catch (err) {
      toast({
        title: 'Failed to enroll',
        description: err.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setEnrollingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Text fontSize="lg" fontWeight="600">Enroll in Campaign</Text>
          <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
            Select a campaign to enroll this contact in
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={3}>
            <InputGroup size="sm">
              <InputLeftElement>
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search campaigns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>

            {loading ? (
              <Center py={8}>
                <Spinner size="md" color="blue.400" />
              </Center>
            ) : availableCampaigns.length === 0 ? (
              <Center py={8}>
                <VStack spacing={2}>
                  <Icon as={FiZap} boxSize="24px" color="gray.300" />
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {enrolledCount > 0
                      ? 'Already enrolled in all available campaigns'
                      : 'No enabled campaigns available'}
                  </Text>
                </VStack>
              </Center>
            ) : (
              availableCampaigns.map((campaign) => (
                <Box
                  key={campaign.id}
                  p={3}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="md"
                  _hover={{ bg: hoverBg }}
                  transition="background 0.1s"
                >
                  <HStack justify="space-between">
                    <HStack spacing={3} flex="1" minW={0}>
                      <Icon as={FiZap} color="blue.400" flexShrink={0} />
                      <VStack align="start" spacing={0} minW={0}>
                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                          {campaign.template?.name || 'Unnamed Campaign'}
                        </Text>
                        {campaign.template?.description && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {campaign.template.description}
                          </Text>
                        )}
                        {campaign.template?.triggerType && (
                          <Badge fontSize="2xs" colorScheme="gray" variant="subtle" mt={0.5}>
                            {campaign.template.triggerType.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </VStack>
                    </HStack>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      leftIcon={<FiPlus />}
                      isLoading={enrollingId === campaign.id}
                      onClick={() => handleEnroll(campaign.id)}
                      flexShrink={0}
                    >
                      Enroll
                    </Button>
                  </HStack>
                </Box>
              ))
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
